import * as React from 'react';
import { KiteIcon } from '@kite/react-kite';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { SmartLink } from '../SmartLink/SmartLink';
import { AnalyticsController } from '../../layout/Analytics';
import { useInlineImages } from '../../shared/useInlineImages';
import './LeaderHighlight.scss';

export interface LeaderHighlightInputs {
    imgData: {
        alt: string;
        verticalAlign: string;
    };
    gImage: IGatsbyImageData;
    mainTitle: string;
    subTitle: string;
    textContent?: string;
    socials?: {
        social: string;
        handle: string;
    }[];
    cta?: {
        title: string;
        uri: string;
        download: boolean;
    }[];
    isEmbedded?: boolean;
}

export function LeaderHighlight({ inputs }: { inputs: LeaderHighlightInputs }) {
    const {
        imgData,
        gImage,
        mainTitle,
        subTitle,
        textContent,
        socials,
        cta,
        isEmbedded = false,
    } = inputs;
    return (
        <>
            <section
                className={`paragraph__leadership_promotion ${
                    isEmbedded
                        ? `paragraph__leadership_promotion--embedded`
                        : ``
                }`}
            >
                <div
                    className={`leader-hl ${
                        isEmbedded ? `leader-hl--embedded` : `kite-container`
                    }`}
                >
                    {gImage && (
                        <div className={`leader-hl__img-wrapper`}>
                            <GatsbyImage
                                className="leader-hl__img"
                                alt={imgData.alt}
                                image={gImage}
                                key={imgData.alt}
                            />
                        </div>
                    )}
                    <div className="leader-hl__content">
                        <h1 className="leader-hl__name">{mainTitle}</h1>
                        {subTitle && (
                            <h2 className="leader-hl__position">
                                {subTitle.toUpperCase()}
                            </h2>
                        )}
                        <span className="decorative-hr" />
                        {textContent && (
                            <div
                                className="typestack-body-1 leader-hl__summary "
                                dangerouslySetInnerHTML={{
                                    __html: useInlineImages(textContent),
                                }}
                            />
                        )}
                        {socials && (
                            <SocialDownloadBlock
                                socials={socials}
                                cta={cta}
                                classNames={[`show-xxl`]}
                                mainTitle={mainTitle}
                            />
                        )}
                        {isEmbedded &&
                            cta.map((ctaItem, idx) => (
                                <SmartLink
                                    key={ctaItem.uri ?? idx}
                                    url={ctaItem.uri}
                                    download={ctaItem.download}
                                    newWindow
                                    classNames={[`cta-link`]}
                                >
                                    {ctaItem.title}
                                    <KiteIcon
                                        icon="ki-arrow-right"
                                        size="1.5rem"
                                    />
                                </SmartLink>
                            ))}
                    </div>
                </div>
            </section>
            {socials && (
                <SocialDownloadBlock
                    socials={socials}
                    cta={cta}
                    classNames={[`hide-xxl`]}
                    mainTitle={mainTitle}
                />
            )}
        </>
    );
}

const SocialDownloadBlock = ({
    socials,
    cta,
    classNames,
    mainTitle,
}: {
    socials: { social: string; handle: string }[];
    cta: {
        title: string;
        uri: any;
        download: boolean;
    }[];
    classNames?: string[];
    mainTitle: string;
}) => {
    let socialLinks = 0;
    const socialIcons = socials.map((social) => {
        switch (social.social) {
            case `linkedin`:
                social.handle ? socialLinks++ : null;
                return social.handle ? (
                    <SmartLink url={social.handle} key={social.handle}>
                        <KiteIcon
                            ariaLabel="linkedin-icon"
                            icon="ki-linkedin"
                            color="#000"
                            size="2rem"
                        />
                    </SmartLink>
                ) : null;
            case `x`:
                social.handle ? socialLinks++ : null;
                return social.handle ? (
                    <SmartLink url={social.handle} key={social.handle}>
                        <KiteIcon
                            ariaLabel="X-icon"
                            icon="ki-twitter"
                            color="#000"
                            size="2rem"
                        />
                    </SmartLink>
                ) : null;
            case `youtube`:
                social.handle ? socialLinks++ : null;
                return social.handle ? (
                    <SmartLink url={social.handle} key={social.handle}>
                        <KiteIcon
                            ariaLabel="X-icon"
                            icon="ki-youtube"
                            color="#000"
                            size="2rem"
                        />
                    </SmartLink>
                ) : null;
            default:
                return null;
        }
    });
    return (
        <>
            <div className={`social ${classNames?.join(` `)}`}>
                {socialLinks > 0 && (
                    <>
                        <p className="typestack-clickable">Follow On Social</p>
                        <div className="social__icons">{socialIcons}</div>
                    </>
                )}
                <div className="download">
                    {cta.length > 0 &&
                        cta[0].title &&
                        cta.map((link) => (
                            <SmartLink
                                key={link.uri}
                                url={link.uri}
                                classNames={[`download__link cta-link`]}
                                ariaLabel={`${link.title} - ${mainTitle}`}
                                download={link.download}
                                onClick={() => {
                                    AnalyticsController.track({
                                        event: `component click`,
                                        componentLink: `${mainTitle}: ${link.title}`,
                                    });
                                }}
                            >
                                {link.title}
                                <KiteIcon
                                    color="var(--kite-color-primary)"
                                    icon="ki-download"
                                    title="One two three o'clock"
                                    inline
                                    size="1.5rem"
                                />
                            </SmartLink>
                        ))}
                    <p className="superscript">
                        All profile images downloaded from this site may be used
                        for editorial purposes only.
                    </p>
                </div>
            </div>
        </>
    );
};
